// src/components/dashboard/OverviewPanel.jsx
// Shared client Overview — used inside ClientDashboard (fed by its state)
// and standalone on the home page (self-contained: fetches user, stats,
// and manages the avatar itself). Navigation is pluggable:
//   - pass onNavigate(tab, category) to drive dashboard tab switching
//   - omit it and the panel routes to /dashboard?tab=...&cat=...

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './overview.css';

const AVATAR_OPTIONS = [
  { id: 'initials', type: 'initials', label: 'Initials' },
  { id: 'avatar1', type: 'dicebear', style: 'avataaars', seed: 'Felix' },
  { id: 'avatar2', type: 'dicebear', style: 'avataaars', seed: 'Aneka' },
  { id: 'avatar3', type: 'dicebear', style: 'avataaars', seed: 'Jasper' },
  { id: 'avatar4', type: 'dicebear', style: 'fun-emoji', seed: 'happy' },
  { id: 'avatar5', type: 'dicebear', style: 'fun-emoji', seed: 'cool' },
  { id: 'avatar6', type: 'dicebear', style: 'lorelei', seed: 'Felix' },
  { id: 'avatar7', type: 'dicebear', style: 'notionists', seed: 'business' },
  { id: 'avatar8', type: 'dicebear', style: 'personas', seed: 'developer' },
];

const getAvatarUrl = (avatar) => {
  if (!avatar || avatar.type === 'initials') return null;
  return `https://api.dicebear.com/7.x/${avatar.style}/svg?seed=${avatar.seed}`;
};

const OverviewPanel = ({ user: userProp, stats: statsProp, onNavigate }) => {
  const navigate = useNavigate();

  // ----- user (prop, else localStorage) -----
  const [user] = useState(() => {
    if (userProp) return userProp;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  // ----- stats (prop, else fetch) -----
  const [fetchedStats, setFetchedStats] = useState({
    projects: 0,
    invoices: 0,
    pending: 0,
    messages: 0,
  });
  const stats = statsProp || fetchedStats;

  useEffect(() => {
    if (statsProp) return; // dashboard already supplies stats
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const auth = { headers: { Authorization: `Bearer ${token}` } };

        const [pRes, iRes] = await Promise.all([
          fetch(`${apiUrl}/api/projects`, auth),
          fetch(`${apiUrl}/api/invoices`, auth),
        ]);

        const next = { projects: 0, invoices: 0, pending: 0, messages: 0 };
        if (pRes.ok) {
          const d = await pRes.json();
          next.projects = d.projects?.length || 0;
        }
        if (iRes.ok) {
          const d = await iRes.json();
          next.invoices = d.invoices?.length || 0;
          next.pending = d.invoices?.filter((x) => x.status === 'pending').length || 0;
        }
        if (!cancelled) setFetchedStats(next);
      } catch (err) {
        console.error('OverviewPanel: failed to load stats', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statsProp]);

  // ----- avatar (self-managed) -----
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const avatarPickerRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem('userAvatar');
    if (saved) {
      try {
        setSelectedAvatar(JSON.parse(saved));
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (avatarPickerRef.current && !avatarPickerRef.current.contains(e.target)) {
        setShowAvatarPicker(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    localStorage.setItem('userAvatar', JSON.stringify(avatar));
    setShowAvatarPicker(false);
  };

  // ----- name helpers -----
  const getInitials = () => {
    const f = user?.firstName || user?.profile?.firstName || '';
    const l = user?.lastName || user?.profile?.lastName || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || 'U';
  };
  const getUserName = () => {
    const f = user?.firstName || user?.profile?.firstName || '';
    const l = user?.lastName || user?.profile?.lastName || '';
    return `${f} ${l}`.trim() || user?.email || 'there';
  };
  const firstName = (getUserName() || '').split(' ')[0] || 'there';

  // ----- navigation (dashboard tab switch OR route into dashboard) -----
  const go = (tab, cat = null) => {
    if (onNavigate) {
      onNavigate(tab, cat);
    } else {
      navigate(`/dashboard?tab=${tab}${cat ? `&cat=${cat}` : ''}`);
    }
  };
  const mailto = (subject) =>
    `mailto:hello@spinosoftbits.com${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;

  return (
    <div className="ov">
      {/* ===== Hero command bar ===== */}
      <header className="ov-hero">
        <div className="ov-hero__intro">
          <span className="ov-eyebrow">SpinoSoftBits · Client Portal</span>
          <h1 className="ov-hero__title">Welcome back, <span>{firstName}</span></h1>
          <p className="ov-hero__sub">Here's where your projects stand today.</p>
        </div>

        <div className="ov-vitals" role="group" aria-label="Account summary">
          <button className="ov-vital" onClick={() => go('projects')}>
            <span className="ov-vital__num">{stats.projects}</span>
            <span className="ov-vital__label">Active projects</span>
          </button>
          <button className="ov-vital" onClick={() => go('invoices')}>
            <span className="ov-vital__num">{stats.invoices}</span>
            <span className="ov-vital__label">Invoices</span>
          </button>
          <button className="ov-vital" onClick={() => go('projects')}>
            <span className="ov-vital__num">
              {stats.pending}
              {stats.pending > 0 && <i className="ov-vital__dot" />}
            </span>
            <span className="ov-vital__label">Pending</span>
          </button>
          <button className="ov-vital" onClick={() => { window.location.href = mailto(); }}>
            <span className="ov-vital__num">{stats.messages}</span>
            <span className="ov-vital__label">Messages</span>
          </button>
        </div>
      </header>

      {/* ===== Body ===== */}
      <div className="ov-grid">
        {/* Identity */}
        <aside className="ov-card ov-identity" ref={avatarPickerRef}>
          <div
            className="user__avatar user__avatar--clickable ov-identity__avatar"
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            title="Click to change avatar"
          >
            {selectedAvatar && selectedAvatar.type === 'dicebear' ? (
              <img src={getAvatarUrl(selectedAvatar)} alt="Avatar" className="avatar__image" />
            ) : (
              <span>{getInitials()}</span>
            )}
            <div className="avatar__edit-icon">
              <i className='uil uil-camera'></i>
            </div>
          </div>

          {showAvatarPicker && (
            <div className="avatar__picker">
              <h4>Choose Avatar</h4>
              <div className="avatar__options">
                {AVATAR_OPTIONS.map((avatar) => (
                  <button
                    key={avatar.id}
                    className={`avatar__option ${selectedAvatar?.id === avatar.id ? 'active' : ''}`}
                    onClick={() => handleAvatarSelect(avatar)}
                  >
                    {avatar.type === 'initials' ? (
                      <span className="avatar__initials">{getInitials()}</span>
                    ) : (
                      <img src={getAvatarUrl(avatar)} alt={avatar.id} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <h2 className="ov-identity__name">{getUserName()}</h2>
          <p className="ov-identity__email">{user?.email || 'client@spinosoftbits.com'}</p>

          <button className="ov-identity__link" onClick={() => go('projects')}>
            View my projects <i className="uil uil-arrow-right"></i>
          </button>
        </aside>

        {/* Pick a track */}
        <section className="ov-card ov-start">
          <div className="ov-start__head">
            <span className="ov-eyebrow ov-eyebrow--muted">Get started</span>
            <h2 className="ov-start__title">Where are you headed?</h2>
            <p className="ov-start__sub">
              Pick a track and we'll open the right packages — or request something fully custom.
            </p>
          </div>

          <div className="ov-tracks">
            <button className="ov-track" onClick={() => go('customize', 'entrepreneur')}>
              <span className="ov-track__icon">🚀</span>
              <span className="ov-track__body">
                <span className="ov-track__name">Entrepreneur Launchpad</span>
                <span className="ov-track__desc">Get online, fast</span>
              </span>
              <i className="uil uil-arrow-right ov-track__arrow"></i>
            </button>

            <button className="ov-track" onClick={() => go('customize', 'smallbusiness')}>
              <span className="ov-track__icon">🏪</span>
              <span className="ov-track__body">
                <span className="ov-track__name">Small Business Studio</span>
                <span className="ov-track__desc">Grow &amp; automate</span>
              </span>
              <i className="uil uil-arrow-right ov-track__arrow"></i>
            </button>

            <button className="ov-track" onClick={() => go('customize', 'enterprise')}>
              <span className="ov-track__icon">🖥️</span>
              <span className="ov-track__body">
                <span className="ov-track__name">Enterprise Systems Lab</span>
                <span className="ov-track__desc">APIs &amp; integrations</span>
              </span>
              <i className="uil uil-arrow-right ov-track__arrow"></i>
            </button>

            <button className="ov-track ov-track--custom" onClick={() => go('customize')}>
              <span className="ov-track__icon">⚡</span>
              <span className="ov-track__body">
                <span className="ov-track__name">Custom Software</span>
                <span className="ov-track__desc">Something bespoke</span>
              </span>
              <i className="uil uil-arrow-right ov-track__arrow"></i>
            </button>
          </div>

          <div className="ov-start__foot">
            <span><i className="uil uil-headphones-alt"></i> Direct support</span>
            <span><i className="uil uil-rocket"></i> Fast delivery</span>
            <span><i className="uil uil-shield-check"></i> Secure by default</span>
          </div>
        </section>
      </div>

      {/* ===== Utility row ===== */}
      <section className="ov-actions">
        <button className="ov-action" onClick={() => go('customize')}>
          <i className="uil uil-apps"></i>
          <span>Browse services</span>
        </button>
        <button className="ov-action" onClick={() => go('invoices')}>
          <i className="uil uil-invoice"></i>
          <span>View invoices</span>
        </button>
        <button className="ov-action" onClick={() => { window.location.href = mailto(); }}>
          <i className="uil uil-comment-alt-message"></i>
          <span>Message us</span>
        </button>
        <button className="ov-action" onClick={() => { window.location.href = mailto('Help'); }}>
          <i className="uil uil-question-circle"></i>
          <span>Get help</span>
        </button>
      </section>
    </div>
  );
};

export default OverviewPanel;